import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'reactstrap';
import ActionButton from '../../../../Components/Common/ActionButton';
import axios from 'axios';
import notify from '../../../../utils/notify';
import { confirmDelete } from '../../../../utils/confirm';
import { api } from '../../../../config';
import { DetailItem, SectionCard } from '../../../../shared/offer-domain/ui';
import { COUNTER_ACTION_BTN_STYLE } from '../../../../shared/offer-domain/constants';
import ClauseCard from './ClauseCard';
import {
  CounterPaymentTermsModal,
  CounterComplianceModal,
} from './PaymentComplianceModals';
import {
  pickLatestCounter,
  isSameId,
  isSameHtml,
  CounterDetailItem,
  CounterValueBlock,
  resolvePaymentSelectLabel,
  resolvePaymentCurrencyLabel,
} from '../../../../shared/offer-domain/payment';

export const PAYMENT_RESPOND_KEY = 'counterPaymentTerm';
export const COMPLIANCE_RESPOND_KEY = 'counterCompliance';

const unwrapItems = result => {
  const data = result?.data ?? result;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data)) return data;
  return [];
};

const mapSelectItems = (list, getLabel) =>
  (Array.isArray(list) ? list : [])
    .map(item => ({
      value: item?.id ?? item?.Id ?? '',
      label: getLabel(item),
    }))
    .filter(item => item.value && item.label);

const itemName = item => item?.name ?? item?.Name ?? '';

const getLabel = (items, value, entity) =>
  resolvePaymentSelectLabel(entity, items, value);

const fetchJson = async (url, token) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const CounterButton = ({ onClick, label = 'Counter' }) => (
  <ActionButton
    variant='secondary'
    size='sm'
    icon='ri-edit-line'
    className='flex-shrink-0'
    style={COUNTER_ACTION_BTN_STYLE}
    onClick={onClick}
  >
    {label}
  </ActionButton>
);

const DeleteCounterButton = ({ onClick }) => (
  <ActionButton
    variant='destructive'
    size='sm'
    icon='ri-delete-bin-line'
    confirm={false}
    className='flex-shrink-0'
    onClick={onClick}
  >
    Delete
  </ActionButton>
);

const CounterActions = ({ hasCounter, onEdit, onDelete }) => (
  <div className='d-flex align-items-center gap-2 flex-shrink-0'>
    <CounterButton label={hasCounter ? 'Edit' : 'Counter'} onClick={onEdit} />
    {hasCounter && onDelete && <DeleteCounterButton onClick={onDelete} />}
  </div>
);

const SectionTitle = ({ title, subtitle, action }) => (
  <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
    <div>
      <h5 className='fw-bold text-dark mb-1'>{title}</h5>
      {subtitle && <p className='text-muted small mb-0'>{subtitle}</p>}
    </div>
    {action}
  </div>
);

const toNullable = value => {
  if (value == null) return null;
  return String(value).trim() === '' ? null : value;
};

const pickId = (id, entity) => id || entity?.id || entity?.Id || '';

const PaymentComplianceSection = ({
  form,
  token,
  editable,
  canRespond,
  formMain,
  refreshProcurement,
  typeResponses = {},
  draftNotes,
  expandedClauses,
  isRespondSaving,
  isRespondLoading,
  handleDraftChange,
  handleClauseSecondaryAction,
  handleClauseSave,
  toggleClauseExpand,
}) => {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);
  const [invoiceQuantityOptions, setInvoiceQuantityOptions] = useState([]);
  const [modeOptions, setModeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [lawOptions, setLawOptions] = useState([]);
  const [arbitrationOptions, setArbitrationOptions] = useState([]);
  const [paymentDraft, setPaymentDraft] = useState({
    invoicingQuantityId: '',
    modeInvoicingQuantityId: '',
    paymentCurrencyId: '',
    descriptionOfPaymentTerms: '',
  });
  const [complianceDraft, setComplianceDraft] = useState({
    lawId: '',
    arbitrationId: '',
    otherTermsAndCondition: '',
    additionalNotes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!token) return;

    const loadInvoiceQuantity = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/invoicing-quantity`,
          token
        );
        setInvoiceQuantityOptions(
          mapSelectItems(unwrapItems(result), itemName)
        );
      } catch {
        setInvoiceQuantityOptions([]);
      }
    };

    const loadCurrency = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/paymentcurrency`, token);
        setCurrencyOptions(
          mapSelectItems(unwrapItems(result), item =>
            item?.currencyName || item?.CurrencyName
              ? `${item.currencyName ?? item.CurrencyName} (${item.currencyCode ?? item.CurrencyCode})`
              : itemName(item)
          )
        );
      } catch {
        setCurrencyOptions([]);
      }
    };

    const loadLaw = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/law`, token);
        setLawOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setLawOptions([]);
      }
    };

    const loadArbitration = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/law/getAll-arbitration`,
          token
        );
        setArbitrationOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setArbitrationOptions([]);
      }
    };

    const loadModes = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity`,
          token
        );
        setModeOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setModeOptions([]);
      }
    };

    loadInvoiceQuantity();
    loadModes();
    loadCurrency();
    loadLaw();
    loadArbitration();
  }, [token]);

  const latestPaymentCounter = useMemo(
    () => pickLatestCounter(form.counterPaymentTerm, formMain?.id),
    [form.counterPaymentTerm, formMain?.id]
  );
  const latestComplianceCounter = useMemo(
    () => pickLatestCounter(form.counterCompliance, formMain?.id),
    [form.counterCompliance, formMain?.id]
  );

  const openPaymentModal = () => {
    const source = latestPaymentCounter || form;
    setPaymentDraft({
      invoicingQuantityId: pickId(
        source.invoicingQuantityId,
        source.invoicingQuantity
      ),
      modeInvoicingQuantityId: pickId(
        source.modeInvoicingQuantityId,
        source.modeInvoicingQuantity
      ),
      paymentCurrencyId: pickId(
        source.paymentCurrencyId,
        source.paymentCurrency
      ),
      descriptionOfPaymentTerms: source.descriptionOfPaymentTerms || '',
    });
    setPaymentModalOpen(true);
  };

  const openComplianceModal = () => {
    const source = latestComplianceCounter || form;
    setComplianceDraft({
      lawId: pickId(source.lawId, source.law),
      arbitrationId: pickId(source.arbitrationId, source.arbitration),
      otherTermsAndCondition: source.otherTermsAndCondition || '',
      additionalNotes: source.additionalNotes || '',
    });
    setComplianceModalOpen(true);
  };

  const handlePaymentDraftChange = (field, value) => {
    setPaymentDraft(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'invoicingQuantityId'
        ? { modeInvoicingQuantityId: '' }
        : {}),
    }));
  };

  const handleComplianceDraftChange = (field, value) => {
    setComplianceDraft(prev => ({ ...prev, [field]: value }));
  };

  const authHeaders = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  const saveCounter = async (endpoint, payload, existingId) => {
    const procurementFK = formMain?.ids;
    if (!procurementFK) {
      notify.error(
        'The procurement could not be identified. Return to the list and open it again.'
      );
      return false;
    }
    if (isSaving) return false;

    setIsSaving(true);
    try {
      const url = existingId
        ? `${api.API_URL}/${endpoint}/${existingId}`
        : `${api.API_URL}/${endpoint}`;
      const request = existingId ? axios.put : axios.post;
      await request(url, { procurementFK, ...payload }, authHeaders);
      if (refreshProcurement) await refreshProcurement();
      notify.success(
        existingId
          ? 'Counter offer updated successfully.'
          : 'Counter offer saved successfully.'
      );
      return true;
    } catch (error) {
      // Pesan BE (mis. bukan mitra undangan / counter milik mitra lain) ditampilkan apa adanya.
      notify.apiError(
        error,
        existingId ? 'update the counter offer' : 'save the counter offer'
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCounter = async (endpoint, existingId, label) => {
    if (!existingId) {
      notify.error(
        'The counter offer could not be identified. Refresh the page and try again.'
      );
      return;
    }
    if (isSaving) return;

    const confirmed = await confirmDelete('counter offer', label, {
      message:
        'Your counter offer will be permanently deleted. This cannot be undone.',
    });
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await axios.delete(`${api.API_URL}/${endpoint}/${existingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (refreshProcurement) await refreshProcurement();
      notify.success('Counter offer deleted successfully.');
    } catch (error) {
      notify.apiError(error, 'delete the counter offer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePaymentCounter = async () => {
    const saved = await saveCounter(
      'procurement-counter-payment-term',
      {
        invoicingQuantityId: toNullable(paymentDraft.invoicingQuantityId),
        modeInvoicingQuantityId: toNullable(
          paymentDraft.modeInvoicingQuantityId
        ),
        paymentCurrencyId: toNullable(paymentDraft.paymentCurrencyId),
        descriptionOfPaymentTerms: toNullable(
          paymentDraft.descriptionOfPaymentTerms
        ),
      },
      latestPaymentCounter?.id
    );
    if (saved) setPaymentModalOpen(false);
  };

  const handleSaveComplianceCounter = async () => {
    const saved = await saveCounter(
      'procurement-counter-compliance',
      {
        lawId: toNullable(complianceDraft.lawId),
        arbitrationId: toNullable(complianceDraft.arbitrationId),
        otherTermsAndCondition: toNullable(
          complianceDraft.otherTermsAndCondition
        ),
        additionalNotes: toNullable(complianceDraft.additionalNotes),
      },
      latestComplianceCounter?.id
    );
    if (saved) setComplianceModalOpen(false);
  };

  const respondCardProps = {
    draftNotes,
    expandedClauses,
    formMain,
    isClauseResponsesSaving: isRespondSaving,
    isClauseResponsesLoading: isRespondLoading,
    handleDraftChange,
    handleClauseSecondaryAction,
    handleClauseSave,
    toggleClauseExpand,
    canRespond,
    showHeader: false,
    showDescription: false,
  };

  return (
    <>
      <SectionCard>
        <SectionTitle
          title='Payment Terms'
          subtitle='Choose the payment methods and terms that apply.'
          action={
            editable ? (
              <CounterActions
                hasCounter={Boolean(latestPaymentCounter)}
                onEdit={openPaymentModal}
                onDelete={
                  latestPaymentCounter?.id
                    ? () =>
                        deleteCounter(
                          'procurement-counter-payment-term',
                          latestPaymentCounter.id,
                          'Payment terms counter offer'
                        )
                    : undefined
                }
              />
            ) : null
          }
        />
        <Row className='gy-4 mb-3'>
          <Col lg={4}>
            <DetailItem
              label='Invoicing Quantity'
              value={resolvePaymentSelectLabel(
                form.invoicingQuantity,
                invoiceQuantityOptions,
                form.invoicingQuantityId
              )}
            />
          </Col>
          <Col lg={4}>
            <DetailItem
              label='Mode'
              value={resolvePaymentSelectLabel(
                form.modeInvoicingQuantity,
                modeOptions,
                form.modeInvoicingQuantityId
              )}
            />
          </Col>
          <Col lg={4}>
            <DetailItem
              label='Currency'
              value={resolvePaymentCurrencyLabel(
                form.paymentCurrency,
                form.paymentCurrencyId,
                currencyOptions
              )}
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Description of Payment Term'
              value={form.descriptionOfPaymentTerms}
              isHtml
            />
          </Col>
        </Row>
        {latestPaymentCounter && (
          <CounterValueBlock
            title={editable ? 'Proposed Counter Value' : 'Final Counter Value'}
          >
            <Row className='gy-4'>
              <Col lg={4}>
                <CounterDetailItem
                  label='Invoicing Quantity'
                  value={resolvePaymentSelectLabel(
                    latestPaymentCounter.invoicingQuantity,
                    invoiceQuantityOptions,
                    latestPaymentCounter.invoicingQuantityId
                  )}
                  unchanged={isSameId(
                    latestPaymentCounter.invoicingQuantityId,
                    form.invoicingQuantityId,
                    latestPaymentCounter.invoicingQuantity,
                    form.invoicingQuantity
                  )}
                />
              </Col>
              <Col lg={4}>
                <CounterDetailItem
                  label='Mode'
                  value={resolvePaymentSelectLabel(
                    latestPaymentCounter.modeInvoicingQuantity,
                    modeOptions,
                    latestPaymentCounter.modeInvoicingQuantityId
                  )}
                  unchanged={isSameId(
                    latestPaymentCounter.modeInvoicingQuantityId,
                    form.modeInvoicingQuantityId,
                    latestPaymentCounter.modeInvoicingQuantity,
                    form.modeInvoicingQuantity
                  )}
                />
              </Col>
              <Col lg={4}>
                <CounterDetailItem
                  label='Currency'
                  value={resolvePaymentCurrencyLabel(
                    latestPaymentCounter.paymentCurrency,
                    latestPaymentCounter.paymentCurrencyId,
                    currencyOptions
                  )}
                  unchanged={
                    isSameId(
                      latestPaymentCounter.paymentCurrencyId,
                      form.paymentCurrencyId,
                      latestPaymentCounter.paymentCurrency,
                      form.paymentCurrency
                    ) ||
                    resolvePaymentCurrencyLabel(
                      latestPaymentCounter.paymentCurrency,
                      latestPaymentCounter.paymentCurrencyId,
                      currencyOptions
                    ) ===
                      resolvePaymentCurrencyLabel(
                        form.paymentCurrency,
                        form.paymentCurrencyId,
                        currencyOptions
                      )
                  }
                />
              </Col>
              {!isSameHtml(
                latestPaymentCounter.descriptionOfPaymentTerms,
                form.descriptionOfPaymentTerms
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Description of Payment Term'
                    value={latestPaymentCounter.descriptionOfPaymentTerms}
                    isHtml
                  />
                </Col>
              )}
            </Row>
          </CounterValueBlock>
        )}
        {!editable && (
          <Row className='mt-3'>
            <ClauseCard
              clause={{
                id: PAYMENT_RESPOND_KEY,
                clauseTitle: 'Payment Terms',
                clauseDescription: '',
              }}
              clauseResponses={{
                [PAYMENT_RESPOND_KEY]: typeResponses[PAYMENT_RESPOND_KEY],
              }}
              {...respondCardProps}
            />
          </Row>
        )}
      </SectionCard>

      <SectionCard>
        <SectionTitle
          title='Compliance'
          subtitle='Confirm the compliance requirements for this procurement.'
          action={
            editable ? (
              <CounterActions
                hasCounter={Boolean(latestComplianceCounter)}
                onEdit={openComplianceModal}
                onDelete={
                  latestComplianceCounter?.id
                    ? () =>
                        deleteCounter(
                          'procurement-counter-compliance',
                          latestComplianceCounter.id,
                          'Compliance counter offer'
                        )
                    : undefined
                }
              />
            ) : null
          }
        />
        <Row className='gy-4 mb-3'>
          <Col lg={6}>
            <DetailItem
              label='Law'
              value={getLabel(lawOptions, form.lawId, form.law)}
            />
          </Col>
          <Col lg={6}>
            <DetailItem
              label='Arbitration'
              value={getLabel(
                arbitrationOptions,
                form.arbitrationId,
                form.arbitration
              )}
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Other Terms & Condition'
              value={form.otherTermsAndCondition}
              isHtml
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Additional Notes'
              value={form.additionalNotes}
              isHtml
            />
          </Col>
        </Row>
        {latestComplianceCounter && (
          <CounterValueBlock
            title={editable ? 'Proposed Counter Value' : 'Final Counter Value'}
          >
            <Row className='gy-4'>
              <Col lg={6}>
                <CounterDetailItem
                  label='Law'
                  value={resolvePaymentSelectLabel(
                    latestComplianceCounter.law,
                    lawOptions,
                    latestComplianceCounter.lawId
                  )}
                  unchanged={isSameId(
                    latestComplianceCounter.lawId,
                    form.lawId,
                    latestComplianceCounter.law,
                    form.law
                  )}
                />
              </Col>
              <Col lg={6}>
                <CounterDetailItem
                  label='Arbitration'
                  value={resolvePaymentSelectLabel(
                    latestComplianceCounter.arbitration,
                    arbitrationOptions,
                    latestComplianceCounter.arbitrationId
                  )}
                  unchanged={isSameId(
                    latestComplianceCounter.arbitrationId,
                    form.arbitrationId,
                    latestComplianceCounter.arbitration,
                    form.arbitration
                  )}
                />
              </Col>
              {!isSameHtml(
                latestComplianceCounter.otherTermsAndCondition,
                form.otherTermsAndCondition
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Other Terms & Condition'
                    value={latestComplianceCounter.otherTermsAndCondition}
                    isHtml
                  />
                </Col>
              )}
              {!isSameHtml(
                latestComplianceCounter.additionalNotes,
                form.additionalNotes
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Additional Notes'
                    value={latestComplianceCounter.additionalNotes}
                    isHtml
                  />
                </Col>
              )}
            </Row>
          </CounterValueBlock>
        )}
        {!editable && (
          <Row className='mt-3'>
            <ClauseCard
              clause={{
                id: COMPLIANCE_RESPOND_KEY,
                clauseTitle: 'Compliance',
                clauseDescription: '',
              }}
              clauseResponses={{
                [COMPLIANCE_RESPOND_KEY]: typeResponses[COMPLIANCE_RESPOND_KEY],
              }}
              {...respondCardProps}
            />
          </Row>
        )}
      </SectionCard>

      <CounterPaymentTermsModal
        isOpen={paymentModalOpen}
        toggle={() => setPaymentModalOpen(false)}
        draft={paymentDraft}
        onDraftChange={handlePaymentDraftChange}
        invoiceQuantityOptions={invoiceQuantityOptions}
        modeOptions={modeOptions}
        currencyOptions={currencyOptions}
        onSave={handleSavePaymentCounter}
        isUpdate={Boolean(latestPaymentCounter?.id)}
      />
      <CounterComplianceModal
        isOpen={complianceModalOpen}
        toggle={() => setComplianceModalOpen(false)}
        draft={complianceDraft}
        onDraftChange={handleComplianceDraftChange}
        lawOptions={lawOptions}
        arbitrationOptions={arbitrationOptions}
        onSave={handleSaveComplianceCounter}
        isUpdate={Boolean(latestComplianceCounter?.id)}
      />
    </>
  );
};

export default PaymentComplianceSection;
